import React, { useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Mail, Send, Users, Filter, Eye, AlertCircle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SuperAdminBroadcast = () => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [filter, setFilter] = useState('all');
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(false);
  const navigate = useNavigate();

  const filters = [
    { id: 'all', label: 'All Clients', description: 'Send to everyone' },
    { id: 'free', label: 'Free Users', description: 'Users on free plan' },
    { id: 'paid', label: 'Paid Users', description: 'Users on paid plans' },
    { id: 'active', label: 'Active Users', description: 'Logged in within 7 days' },
    { id: 'inactive', label: 'Inactive Users', description: 'Not logged in for 7+ days' }
  ];

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Please fill in subject and body');
      return;
    }

    if (!window.confirm(`Are you sure you want to send this email to ${filter} clients?`)) {
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.post(
        `${BACKEND_URL}/api/super-admin/broadcast`,
        { subject, body, filter },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Email sent to ${res.data.sent} clients`);
      setSubject('');
      setBody('');
    } catch (error) {
      console.error('Broadcast failed:', error);
      toast.error('Failed to send broadcast email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f1e] p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <button
            onClick={() => navigate('/super-admin')}
            className="flex items-center gap-2 text-[#94a3b8] hover:text-white mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Admin
          </button>
          <h1 className="text-3xl font-bold text-white">Broadcast Email</h1>
          <p className="text-[#94a3b8]">Send announcements to all clients</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Email Composer */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="bg-[#1a2235] border-[#1e3a5f30]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-emerald-400" />
                Compose Email
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-[#94a3b8] text-sm mb-2">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="e.g., New Feature Announcement"
                  className="w-full px-4 py-3 bg-[#0a0f1e] border border-[#1e3a5f] rounded-lg text-white placeholder-[#94a3b8]/50 focus:border-emerald-500 focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-[#94a3b8] text-sm mb-2">Message Body</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Write your message here...

Use clear paragraphs and bullet points for better readability.

- Feature 1
- Feature 2
- Feature 3

Best regards,
AILeadMantra Team"
                  rows={12}
                  className="w-full px-4 py-3 bg-[#0a0f1e] border border-[#1e3a5f] rounded-lg text-white placeholder-[#94a3b8]/50 focus:border-emerald-500 focus:outline-none resize-none"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={() => setPreview(true)}
                  variant="outline"
                  className="border-blue-500/50 text-blue-400 hover:bg-blue-900/30"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
                <Button
                  onClick={handleSend}
                  disabled={sending || !subject.trim() || !body.trim()}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
                >
                  {sending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send to {filter === 'all' ? 'All' : filter} Clients
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter Selection */}
        <div className="space-y-6">
          <Card className="bg-[#1a2235] border-[#1e3a5f30]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Filter className="w-5 h-5 text-violet-400" />
                Recipient Filter
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {filters.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    className={`w-full p-4 rounded-xl text-left transition-colors ${
                      filter === f.id
                        ? 'bg-emerald-500/20 border border-emerald-500/50'
                        : 'bg-[#0a0f1e] border border-[#1e3a5f] hover:border-[#1e3a5f80]'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`font-medium ${filter === f.id ? 'text-emerald-400' : 'text-white'}`}>
                        {f.label}
                      </span>
                      {filter === f.id && (
                        <div className="w-2 h-2 rounded-full bg-emerald-400" />
                      )}
                    </div>
                    <p className="text-xs text-[#94a3b8] mt-1">{f.description}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Tips */}
          <Card className="bg-[#1a2235] border-[#1e3a5f30]">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-400" />
                Tips
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-sm text-[#94a3b8]">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">•</span>
                  Keep subject lines under 50 characters
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">•</span>
                  Include a clear call-to-action
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">•</span>
                  Use bullet points for feature lists
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-400">•</span>
                  Test with "Active Users" filter first
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* Common Templates */}
          <Card className="bg-[#1a2235] border-[#1e3a5f30]">
            <CardHeader>
              <CardTitle className="text-white">Quick Templates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <button
                onClick={() => {
                  setSubject('New Feature: AI-Powered Analytics');
                  setBody(`Hi there!

We're excited to announce a new feature that will help you understand your leads better.

🎉 What's New:
- AI-powered lead scoring
- Predictive analytics dashboard
- Smart automation suggestions

Log in to try it now!

Best,
AILeadMantra Team`);
                }}
                className="w-full p-3 bg-[#0a0f1e] rounded-lg text-left hover:bg-[#1e3a5f30] transition-colors"
              >
                <p className="text-white text-sm">Feature Announcement</p>
                <p className="text-xs text-[#94a3b8]">Announce new features</p>
              </button>
              
              <button
                onClick={() => {
                  setSubject('Scheduled Maintenance Notice');
                  setBody(`Hi,

We will be performing scheduled maintenance on our servers.

📅 Date: [DATE]
⏰ Time: [TIME] IST
⏱️ Duration: Approximately 30 minutes

During this time, the platform may be briefly unavailable.

Thank you for your patience.

AILeadMantra Team`);
                }}
                className="w-full p-3 bg-[#0a0f1e] rounded-lg text-left hover:bg-[#1e3a5f30] transition-colors"
              >
                <p className="text-white text-sm">Maintenance Notice</p>
                <p className="text-xs text-[#94a3b8]">Inform about downtime</p>
              </button>
              
              <button
                onClick={() => {
                  setSubject('Special Offer: Upgrade & Save 20%');
                  setBody(`Hi!

For a limited time, upgrade your plan and get 20% off!

💎 Why Upgrade?
- More leads capacity
- Advanced automation
- Priority support
- Unlimited team members

Use code: UPGRADE20 at checkout.

Offer ends [DATE].

Best,
AILeadMantra Team`);
                }}
                className="w-full p-3 bg-[#0a0f1e] rounded-lg text-left hover:bg-[#1e3a5f30] transition-colors"
              >
                <p className="text-white text-sm">Upgrade Promotion</p>
                <p className="text-xs text-[#94a3b8]">Encourage plan upgrades</p>
              </button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#1a2235] rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-auto"
          >
            <div className="p-6 border-b border-[#1e3a5f30]">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">Email Preview</h3>
                <button
                  onClick={() => setPreview(false)}
                  className="text-[#94a3b8] hover:text-white"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="bg-white rounded-lg p-6 text-gray-800">
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <p className="text-sm text-gray-500">From: AILeadMantra &lt;noreply@aileadmantra.com&gt;</p>
                  <p className="text-sm text-gray-500">To: {filter} clients</p>
                  <p className="font-semibold mt-2">{subject || 'No Subject'}</p>
                </div>
                <div className="whitespace-pre-wrap text-gray-700">
                  {body || 'No content'}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-[#1e3a5f30] flex justify-end gap-3">
              <Button
                onClick={() => setPreview(false)}
                variant="outline"
                className="border-[#1e3a5f] text-[#94a3b8]"
              >
                Close
              </Button>
              <Button
                onClick={() => {
                  setPreview(false);
                  handleSend();
                }}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Send className="w-4 h-4 mr-2" />
                Send Now
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminBroadcast;
